import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Confirm, NativeSelect } from "@/components/common";
import { api } from "@/lib/api";
import { ceilMult, num } from "@/lib/format";

export function CompareTable({ a, b, testId = "compare-table" }) {
  const ids = [...new Set([...a.lines, ...b.lines].map((l) => l.voucher_type_id))];
  const find = (v, id) => v.lines.find((l) => l.voucher_type_id === id);
  const Diff = ({ d }) => (d === 0 ? <span className="text-slate-400">0</span> : (
    <b className={d > 0 ? "text-emerald-700" : "text-rose-700"}>{d > 0 ? <ArrowUp className="inline h-3 w-3" /> : <ArrowDown className="inline h-3 w-3" />}{d > 0 ? "+" : ""}{num(d)}</b>
  ));
  return (
    <div className="xl-wrap">
      <table className="xl" data-testid={testId}>
        <thead><tr><th>Jenis Voucher</th><th className="text-right">{a.label}</th><th className="text-right">{b.label}</th><th className="text-right">Selisih Qty</th><th className="text-right">Selisih Rp</th></tr></thead>
        <tbody>
          {ids.map((id) => {
            const x = find(a, id), y = find(b, id);
            const dq = (y?.qty || 0) - (x?.qty || 0);
            return (
              <tr key={id} className={dq ? "bg-amber-50" : ""}>
                <td>{(y || x).voucher_name}</td><td className="num">{num(x?.qty || 0)}</td>
                <td className={`num ${dq ? "font-bold" : ""}`}>{num(y?.qty || 0)}</td><td className="num"><Diff d={dq} /></td>
                <td className="num"><Diff d={(y?.total || 0) - (x?.total || 0)} /></td>
              </tr>
            );
          })}
        </tbody>
        <tfoot><tr><td>TOTAL (Rp)</td><td className="num">{num(a.total)}</td><td className="num font-bold">{num(b.total)}</td><td /><td className="num"><Diff d={b.total - a.total} /></td></tr></tfoot>
      </table>
    </div>
  );
}

export function RevisionEditor({ inv, title, needReason, needLines = true, submitLabel, onSubmit, onCancel }) {
  const mult = inv.round_multiple || 5;
  const [rows, setRows] = useState(inv.lines.map((l) => ({ voucher_type_id: l.voucher_type_id, voucher_name: l.voucher_name, price: l.price, qty: l.qty })));
  const [types, setTypes] = useState([]);
  const [add, setAdd] = useState("");
  const [notes, setNotes] = useState(inv.notes || "");
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (needLines) api.get("/prices").then((r) => setTypes(r.data.filter((v) => v.active))).catch(() => setTypes([]));
  }, [needLines]);
  const setQty = (i, v) => setRows((s) => s.map((r, j) => (j === i ? { ...r, qty: Math.max(0, parseInt(v, 10) || 0) } : r)));
  const addType = () => {
    const v = types.find((t) => t.id === add);
    if (v) setRows((s) => [...s, { voucher_type_id: v.id, voucher_name: v.name, price: v.price, qty: mult }]);
    setAdd("");
  };
  const lines = rows.map((r) => ({ ...r, qty: ceilMult(r.qty, mult), total: ceilMult(r.qty, mult) * r.price })).filter((r) => r.qty > 0);
  const total = lines.reduce((s, l) => s + l.total, 0);
  const ok = (!needReason || reason.trim().length >= 5) && (!needLines || lines.length > 0);
  return (
    <div className="border border-emerald-300 bg-emerald-50/40 rounded-sm p-3 space-y-2 min-w-0 max-w-full" data-testid="revision-editor">
      <div className="font-display font-semibold text-sm">{title}</div>
      {needLines && (
        <>
          <div className="xl-wrap">
            <table className="xl">
              <thead><tr><th>Jenis Voucher</th><th className="text-right">Qty (kelipatan {mult})</th><th className="text-right">Harga</th><th className="text-right">Total</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.voucher_type_id}>
                    <td>{r.voucher_name}</td>
                    <td className="cell-edit"><input type="number" min={0} step={mult} className="xl-input" value={r.qty} onChange={(e) => setQty(i, e.target.value)} onBlur={() => setQty(i, ceilMult(r.qty, mult))} data-testid={`rev-qty-${i}`} /></td>
                    <td className="num">{num(r.price)}</td><td className="num">{num(ceilMult(r.qty, mult) * r.price)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td colSpan={3}>TOTAL BARU (Rp)</td><td className="num" data-testid="rev-total">{num(total)}</td></tr></tfoot>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <NativeSelect value={add} onChange={(e) => setAdd(e.target.value)} data-testid="rev-add-type">
              <option value="">Tambah jenis voucher…</option>
              {types.filter((t) => !rows.some((r) => r.voucher_type_id === t.id)).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </NativeSelect>
            <Button type="button" size="sm" variant="outline" disabled={!add} onClick={addType} data-testid="rev-add-btn"><Plus className="h-4 w-4 mr-1" />Tambah</Button>
            <span className="text-xs text-slate-500 self-center">Isi 0 untuk menghapus jenis voucher.</span>
          </div>
          <Input placeholder="Catatan invoice" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="rev-notes" />
        </>
      )}
      <Textarea rows={2} placeholder={`Alasan ${needReason ? "(wajib, min. 5 karakter)" : "(opsional)"}`} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="rev-reason" />
      <div className="flex flex-wrap gap-2 justify-end">
        <Button variant="ghost" onClick={onCancel} className="h-11 sm:h-9" data-testid="rev-cancel">Batal</Button>
        <Confirm title={`${submitLabel}?`} description={needLines ? `Total baru Rp ${num(total)} (sebelumnya Rp ${num(inv.total)}).` : "Pengajuan akan dikirim ke penyetuju."}
          onConfirm={() => onSubmit({ lines: lines.map((l) => ({ voucher_type_id: l.voucher_type_id, qty: l.qty })), notes, reason })} testId="rev-submit"
          trigger={<Button disabled={!ok} className="h-11 sm:h-9 bg-emerald-700 hover:bg-emerald-800" data-testid="rev-submit-btn">{submitLabel}</Button>} />
      </div>
    </div>
  );
}
